class TeesPolosProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('[data-tees-polos-form]');
    this.colourMap = this.readColourMap();
    this.variants = this.readVariants();
    this.mergeVariants(this.readCurrentColourVariants());
    this.colourIndex = Number(section.dataset.colourOptionIndex || 0);
    this.sizeIndex = Number(section.dataset.sizeOptionIndex || 1);
    this.selectedColour = section.querySelector('[data-colour-swatches] .is-selected')?.dataset.colourValue
      || this.variants[0]?.options[this.colourIndex]
      || '';
    this.quantities = new Map();
    this.currency = this.getCurrency();
    this.bulkDrawer = section.querySelector('[data-bulk-grid]');
    this.colourVariantCache = new Map();
    this.colourVariantCache.set(this.selectedColour, this.variantsForColour(this.selectedColour));
    this.bulkBatchSize = 16;
    this.bulkLoading = false;
    this.basePrice = this.variants.find((variant) => String(variant.id) === this.form?.querySelector('[data-primary-variant-id]')?.value)?.price
      || this.variants[0]?.price
      || 0;

    this.bindColourPicker();
    this.bindPlacementPicker();
    this.bindGarmentChoice();
    this.bindColourLayout();
    this.applyColourStyles();
    this.bindQuantityGrid();
    this.bindBulkGrid();
    this.bindModals();
    this.bindForm();
    this.bindQuickBar();
    this.bindAccordions();
    this.bindProductScroll();
    this.bindMobileGallery();
    this.moveAfterpayMessage();
    this.applyAfterpayStyles();
    this.bindStickyQuickAdd();
    this.renderSizeGrid();
    this.updateActionState();
    if (new URLSearchParams(window.location.search).get('open') === 'bulk') {
      window.requestAnimationFrame(() => this.section.querySelector('[data-bulk-grid-open]')?.click());
    }
  }

  applyAfterpayStyles() {
    const injectStyles = () => {
      this.section.querySelectorAll('square-placement').forEach((placement) => {
        const root = placement.shadowRoot;
        if (!root || root.querySelector('[data-tees-polos-afterpay-styles]')) return;

        const style = document.createElement('style');
        style.setAttribute('data-tees-polos-afterpay-styles', '');
        style.textContent = `
          .afterpay-main-text,
          .afterpay-main-text * {
            font-family: Inter, sans-serif !important;
            font-size: 12px !important;
            line-height: 14px !important;
            letter-spacing: 0 !important;
          }
          .afterpay-logo.type-badge,
          .afterpay-logo.type-badge svg,
          .afterpay-logo.logo-cashappafterpay,
          .afterpay-logo.logo-cashappafterpay svg {
            width: auto !important;
            height: 15px !important;
            max-height: 15px !important;
          }
        `;
        root.appendChild(style);
      });
    };

    if (window.customElements?.whenDefined) {
      window.customElements.whenDefined('square-placement').then(() => {
        injectStyles();
        window.setTimeout(injectStyles, 250);
      });
    }

    injectStyles();
    [500, 1000, 2000].forEach((delay) => window.setTimeout(injectStyles, delay));
  }

  moveAfterpayMessage() {
    const target = this.section.querySelector('[data-tees-afterpay-target]');
    if (!target) return;
    const afterpayBlock = [...this.section.querySelectorAll('.shopify-app-block')]
      .find((block) => block.querySelector('square-placement'));
    if (afterpayBlock) target.appendChild(afterpayBlock);
  }

  bindMobileGallery() {
    const gallery = this.section.querySelector('[data-tees-mobile-gallery]');
    const main = gallery?.querySelector('[data-tees-mobile-main]');
    const thumbnailRail = gallery?.querySelector('[data-tees-mobile-thumbnails]');
    const slides = [...(gallery?.querySelectorAll('[data-tees-mobile-slide]') || [])];
    const thumbs = [...(gallery?.querySelectorAll('[data-tees-mobile-thumb]') || [])];
    const dots = [...(gallery?.querySelectorAll('.business-card-product__mobile-dots i') || [])];
    if (!gallery || !main || !slides.length) return;

    let activeIndex = 0;
    let scrollTimer;
    const updateThumbnailOverflow = () => {
      if (!thumbnailRail) return;
      gallery.classList.toggle('has-thumbnail-overflow', thumbnailRail.scrollWidth > thumbnailRail.clientWidth + 1);
    };
    const activate = (index, scrollMain = true) => {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));
      thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === activeIndex));
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
      const activeThumb = thumbs[activeIndex];
      if (thumbnailRail && activeThumb) {
        const thumbLeft = activeThumb.offsetLeft - ((thumbnailRail.clientWidth - activeThumb.offsetWidth) / 2);
        thumbnailRail.scrollTo({ left: Math.max(0, thumbLeft), behavior: 'smooth' });
      }
      if (scrollMain) main.scrollTo({ left: activeIndex * main.clientWidth, behavior: 'smooth' });
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => activate(Number(thumb.dataset.teesMobileThumb))));
    gallery.querySelector('[data-tees-mobile-prev]')?.addEventListener('click', () => activate(activeIndex - 1));
    gallery.querySelector('[data-tees-mobile-next]')?.addEventListener('click', () => activate(activeIndex + 1));
    main.addEventListener('scroll', () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const index = Math.round(main.scrollLeft / Math.max(main.clientWidth, 1));
        activate(index, false);
      }, 80);
    }, { passive: true });
    window.addEventListener('resize', updateThumbnailOverflow, { passive: true });
    window.requestAnimationFrame(updateThumbnailOverflow);
    window.addEventListener('load', updateThumbnailOverflow, { once: true });
  }

  bindAccordions() {
    const accordions = [...this.section.querySelectorAll('.business-card-product__accordion')];
    const animations = new WeakMap();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let initiallyOpen = null;

    const setAccordionOpen = (accordion, shouldOpen) => {
      const content = accordion.querySelector('.business-card-product__accordion-content');
      animations.get(accordion)?.cancel();

      if (!content || reduceMotion.matches) {
        accordion.open = shouldOpen;
        return;
      }

      if (shouldOpen) accordion.open = true;
      const startHeight = shouldOpen ? 0 : content.getBoundingClientRect().height;
      const endHeight = shouldOpen ? content.scrollHeight : 0;
      content.style.overflow = 'hidden';

      const animation = content.animate(
        [
          { height: `${startHeight}px`, opacity: shouldOpen ? 0 : 1 },
          { height: `${endHeight}px`, opacity: shouldOpen ? 1 : 0 },
        ],
        { duration: 300, easing: 'ease-in-out' },
      );
      animations.set(accordion, animation);
      animation.onfinish = () => {
        if (!shouldOpen) accordion.open = false;
        content.style.removeProperty('overflow');
        animations.delete(accordion);
      };
    };

    accordions.forEach((accordion) => {
      if (accordion.open) {
        if (initiallyOpen) accordion.open = false;
        else initiallyOpen = accordion;
      }

      accordion.querySelector('summary')?.addEventListener('click', (event) => {
        event.preventDefault();
        const shouldOpen = !accordion.open;
        if (shouldOpen) {
          accordions.forEach((otherAccordion) => {
            if (otherAccordion !== accordion && otherAccordion.open) {
              setAccordionOpen(otherAccordion, false);
            }
          });
        }
        setAccordionOpen(accordion, shouldOpen);
      });
    });
  }

  bindQuickBar() {
    const quickBar = this.section.querySelector('.business-card-product__quick-bar');
    const primaryAddButton = this.section.querySelector('[data-tees-polos-submit]');
    if (!quickBar || !primaryAddButton) return;

    const stickyStart = quickBar.getBoundingClientRect().top + window.scrollY;
    const quickBarStyles = window.getComputedStyle(quickBar);
    const quickBarSpace = quickBar.offsetHeight
      + Number.parseFloat(quickBarStyles.marginTop || 0)
      + Number.parseFloat(quickBarStyles.marginBottom || 0);
    this.section.style.setProperty('--tees-polos-quick-bar-space', `${quickBarSpace}px`);
    let primaryButtonVisible = false;
    let ticking = false;
    const updateStickyState = () => {
      const hasPassedBar = window.scrollY > stickyStart;
      this.section.classList.toggle('has-fixed-quick-bar', hasPassedBar);
      quickBar.classList.toggle('is-scroll-fixed', hasPassedBar);
      quickBar.classList.toggle('is-scroll-visible', hasPassedBar && !primaryButtonVisible);
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateStickyState);
    }, { passive: true });

    window.addEventListener('resize', updateStickyState, { passive: true });

    const primaryButtonObserver = new IntersectionObserver((entries) => {
      primaryButtonVisible = entries[0]?.isIntersecting || false;
      updateStickyState();
    }, { threshold: 0.05 });
    primaryButtonObserver.observe(primaryAddButton);

    updateStickyState();
  }

  bindProductScroll() {
    const layout = this.section.querySelector('.business-card-product__layout');
    const gallery = this.section.querySelector('.business-card-product__gallery');
    const details = this.section.querySelector('.business-card-product__details');
    if (!layout || !gallery || !details) return;

    let frame;

    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        gallery.style.top = '';
        details.style.top = '';
        gallery.classList.remove('is-product-scroll-sticky');
        details.classList.remove('is-product-scroll-sticky');

        if (!window.matchMedia('(min-width: 990px)').matches) return;

        const galleryHeight = gallery.offsetHeight;
        const detailsHeight = details.offsetHeight;
        const viewportHeight = window.innerHeight;
        const bottomClearance = 40;
        let stickyElement = null;

        if (galleryHeight > detailsHeight) {
          stickyElement = details;
        } else if (detailsHeight > galleryHeight) {
          stickyElement = gallery;
        }

        if (stickyElement) {
          const stickyOffset = Math.max(stickyElement.offsetHeight - viewportHeight + bottomClearance, 0);
          stickyElement.style.top = `${-stickyOffset}px`;
          stickyElement.classList.add('is-product-scroll-sticky');
        }
      });
    };

    const observer = new ResizeObserver(update);
    observer.observe(gallery);
    observer.observe(details);
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  readVariants() {
    try {
      return JSON.parse(this.section.querySelector('[data-tees-polos-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Unable to read tees and polos variants.', error);
      return [];
    }
  }

  readCurrentColourVariants(scope = this.section) {
    try {
      return JSON.parse(scope.querySelector('[data-tees-polos-current-colour-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Unable to read selected colour variants.', error);
      return [];
    }
  }

  mergeVariants(variants) {
    variants.forEach((variant) => {
      const index = this.variants.findIndex((item) => String(item.id) === String(variant.id));
      if (index === -1) this.variants.push(variant);
      else this.variants[index] = variant;
    });
  }

  getCurrency() {
    return window.Shopify?.currency?.active || 'NZD';
  }

  getSizes() {
    return [...new Set(this.variants.map((variant) => variant.options[this.sizeIndex]).filter(Boolean))];
  }

  getColours() {
    return [...this.section.querySelectorAll('[data-colour-value]')].map((swatch) => swatch.dataset.colourValue);
  }

  readColourMap() {
    return [...document.querySelectorAll('[data-tees-polos-colour-map]')].reduce((colours, element) => {
      let value = '';
      try {
        value = JSON.parse(element.textContent || '""');
      } catch (error) {
        console.error('Unable to read the theme colour map.', error);
      }

      String(value).split(/\r?\n/).forEach((line) => {
        const separator = line.indexOf(':');
        if (separator < 1) return;
        const name = line.slice(0, separator).trim().toLowerCase();
        const code = line.slice(separator + 1).trim();
        if (name && code) colours[name] = code;
      });
      return colours;
    }, {});
  }

  colourHex(name) {
    const colours = {
      white: '#ffffff', natural: '#f6f1e5', butter: '#f5edc8', ecru: '#e4e0d5', lime: '#dce79b',
      lemonade: '#f3df83', yellow: '#efbd38', sunset: '#f5bd63', gold: '#f5a62d', mustard: '#e3a52f',
      orange: '#ef6b22', autumn: '#ca4328', fire: '#df2520', orchid: '#dfd9e0', 'pale pink': '#f3cabb',
      pink: '#edcfd2', bubblegum: '#ef9aaa', 'charity pink': '#d85d8c', clay: '#9d603a', copper: '#a94d2d',
      red: '#cb121b', cardinal: '#a81b25', burgundy: '#692727', purple: '#653c72', berry: '#a04c5e',
      mauve: '#8b7a80', lapis: '#687bb1', smoke: '#98a1a5', powder: '#98a2b5', 'slate blue': '#7997a1',
      'carolina blue': '#91b7da', 'fog blue': '#7d98b6', 'arctic blue': '#378dbd', hydro: '#3e8dbb',
      'bright royal': '#3f63a0', cobalt: '#24355e', 'midnight blue': '#36384c', 'petrol blue': '#3e4c55',
      navy: '#1d2435', seafoam: '#e7f2f2', 'pale blue': '#adc2c7', mineral: '#78938e', citrus: '#a9e761',
      pistachio: '#abb490', safari: '#a39b7d', eucalyptus: '#9c9a76', moss: '#919047', kiwi: '#889a68',
      sage: '#758d7c', topaz: '#51ad98', 'kelly green': '#007f53', cypress: '#525f50', charlotte: '#00829a',
      atlantic: '#0d6065', jade: '#005c53', 'forest green': '#27603d', black: '#000000', army: '#56533a',
      coal: '#333333', charcoal: '#474747', bone: '#d5d2d0', cocoa: '#966952', 'dark chocolate': '#3c2b24',
      camel: '#b5863f', khaki: '#b29166', sand: '#b5ae9b', granite: '#9f9fa1', 'grey marle': '#a4a5a5',
      'light grey': '#bbb9b3', mushroom: '#b8aa92', shadow: '#616161', 'pine green': '#173c32',
      'sea spray': '#a8c9c0', walnut: '#705c49', 'pale pink': '#f3cabb'
    };
    const key = String(name).toLowerCase();
    return this.colourMap[key] || colours[key] || '#d9d9d9';
  }

  contrastColour(hex) {
    const value = String(hex || '').replace('#', '');
    const normalized = value.length === 3 ? value.split('').map((character) => character + character).join('') : value;
    if (!/^[0-9a-f]{6}$/i.test(normalized)) return '#111111';
    const red = parseInt(normalized.slice(0, 2), 16);
    const green = parseInt(normalized.slice(2, 4), 16);
    const blue = parseInt(normalized.slice(4, 6), 16);
    return ((red * 299 + green * 587 + blue * 114) / 1000) > 155 ? '#111111' : '#ffffff';
  }

  applyColourStyles() {
    this.section.querySelectorAll('[data-colour-value]').forEach((swatch) => {
      const circle = swatch.querySelector('span:first-child');
      if (circle) circle.style.backgroundColor = this.colourHex(swatch.dataset.colourValue);
    });
  }

  variantsForColour(colour) {
    return this.variants.filter((variant) => variant.options[this.colourIndex] === colour);
  }

  maximumQuantity(variant) {
    if (!variant?.available) return 0;
    if (!variant.inventoryManagement || variant.inventoryPolicy === 'continue') return '';
    return Math.max(variant.inventoryQuantity, 0);
  }

  bindColourPicker() {
    const swatches = [...this.section.querySelectorAll('[data-colour-value]')];
    const moreButton = this.section.querySelector('[data-more-colours]');
    const moreButtonCount = moreButton?.querySelector('[data-more-colours-count]');
    const moreButtonLabel = moreButton?.querySelector('[data-more-colours-label]');
    const additionalColourCount = moreButtonCount?.textContent.trim() || '';
    const mobileColourLayout = window.matchMedia('(max-width: 989px)');
    let showAllColours = false;

    const updateColourVisibility = (query = '') => {
      swatches.forEach((swatch) => {
        const matchesSearch = query === '' || swatch.dataset.colourValue.toLowerCase().includes(query);
        const hiddenExtra = mobileColourLayout.matches
          && query === ''
          && swatch.hasAttribute('data-extra-colour')
          && !showAllColours;
        swatch.hidden = !matchesSearch || hiddenExtra;
      });
      if (moreButton) {
        moreButton.hidden = !mobileColourLayout.matches || query !== '';
        moreButton.setAttribute('aria-expanded', String(showAllColours));
        moreButton.classList.toggle('is-expanded', showAllColours);
        if (moreButtonCount) moreButtonCount.textContent = showAllColours ? '–' : additionalColourCount;
        if (moreButtonLabel) moreButtonLabel.textContent = showAllColours ? 'Less Colours' : 'More Colours';
      }
    };

    swatches.forEach((button) => {
      button.addEventListener('click', async () => {
        const colourChanged = this.selectedColour !== button.dataset.colourValue;
        if (colourChanged) this.quantities.clear();
        this.selectedColour = button.dataset.colourValue;
        this.section.querySelectorAll('[data-colour-value]').forEach((swatch) => {
          const selected = swatch === button;
          swatch.classList.toggle('is-selected', selected);
          swatch.setAttribute('aria-pressed', String(selected));
        });

        const label = this.section.querySelector('[data-selected-colour]');
        if (label) label.textContent = this.selectedColour;
        const stickyColour = this.section.querySelector('[data-sticky-colour-select]');
        if (stickyColour) stickyColour.value = this.selectedColour;
        await this.loadColourVariants(button);
        this.renderSizeGrid();
        this.updatePrimaryVariant();
        this.updateActionState();
        this.refreshStickySizes();
      });
    });

    this.section.querySelector('[data-colour-search]')?.addEventListener('input', (event) => {
      const query = event.target.value.trim().toLowerCase();
      updateColourVisibility(query);
    });

    moreButton?.addEventListener('click', () => {
      showAllColours = !showAllColours;
      updateColourVisibility();
    });

    mobileColourLayout.addEventListener('change', () => updateColourVisibility(
      this.section.querySelector('[data-colour-search]')?.value.trim().toLowerCase() || ''
    ));

    updateColourVisibility();
  }

  async loadColourVariants(button) {
    const colour = button?.dataset.colourValue;
    const optionValueId = button?.dataset.optionValueId;
    if (!colour || !optionValueId || this.colourVariantCache.has(colour)) return;

    const grid = this.section.querySelector('[data-size-grid]');
    grid?.classList.add('is-loading');

    try {
      const url = new URL(this.section.dataset.productUrl, window.location.origin);
      url.searchParams.set('section_id', this.section.dataset.sectionId);
      url.searchParams.set('option_values', optionValueId);
      const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Unable to load ${colour} variants.`);
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const variants = this.readCurrentColourVariants(documentFragment);
      this.mergeVariants(variants);
      this.colourVariantCache.set(colour, variants);
    } catch (error) {
      this.showError(error.message);
    } finally {
      grid?.classList.remove('is-loading');
    }
  }

  bindPlacementPicker() {
    const inputs = [...this.section.querySelectorAll('[data-placement-variant-id]')];
    if (!inputs.length) return;

    const artwork = this.section.querySelector('[data-placement-artwork]');
    const artworkInput = this.section.querySelector('[data-placement-artwork-input]');
    const uploadLabel = this.section.querySelector('[data-placement-upload-label]');
    const defaultUploadLabel = uploadLabel?.innerHTML || '';
    const updatePlacementState = () => {
      const hasPlacement = Boolean(this.selectedPlacement());
      if (artwork) artwork.hidden = !hasPlacement;
      if (!hasPlacement) artwork?.classList.remove('is-invalid');
      if (!hasPlacement && artworkInput) artworkInput.value = '';
      if (!hasPlacement && uploadLabel) uploadLabel.innerHTML = defaultUploadLabel;
      this.updateDisplayedPrice();
    };

    inputs.forEach((input) => {
      input.addEventListener('change', updatePlacementState);
    });

    artworkInput?.addEventListener('change', () => {
      if (!uploadLabel) return;
      uploadLabel.textContent = artworkInput.files?.[0]?.name || '';
      if (!artworkInput.files?.[0]) uploadLabel.innerHTML = defaultUploadLabel;
      artwork?.classList.remove('is-invalid');
    });

    updatePlacementState();
  }

  selectedPlacement() {
    return this.selectedPlacements()[0] || null;
  }

  selectedPlacements() {
    return [...this.section.querySelectorAll('[data-placement-variant-id]:checked')];
  }

  bindGarmentChoice() {
    const choices = [...this.section.querySelectorAll('[data-garment-option]')];
    const fields = this.section.querySelector('[data-branding-fields]');
    if (!choices.length || !fields) return;

    const updateGarmentChoice = () => {
      const branded = this.section.querySelector('[data-garment-option="branded"]')?.checked || false;
      fields.hidden = !branded;
      fields.querySelectorAll('[data-branding-option], [data-placement-variant-id]').forEach((input) => {
        input.disabled = !branded;
      });

      if (branded && !fields.querySelector('[data-placement-variant-id]:checked')) {
        const firstAvailablePlacement = fields.querySelector('[data-placement-variant-id]:not(:disabled)');
        if (firstAvailablePlacement) {
          firstAvailablePlacement.checked = true;
          firstAvailablePlacement.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else if (!branded) {
        fields.querySelectorAll('[data-placement-variant-id]:checked').forEach((input) => {
          input.checked = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      this.updateDisplayedPrice();
    };

    choices.forEach((choice) => choice.addEventListener('change', updateGarmentChoice));
    updateGarmentChoice();
  }

  updateDisplayedPrice() {
    const placementPrice = this.selectedPlacements().reduce(
      (total, placement) => total + Number(placement.dataset.placementPrice || 0),
      0
    );
    const selectedColourVariantIds = new Set(
      this.variantsForColour(this.selectedColour).map((variant) => String(variant.id))
    );
    const selectedQuantities = [...this.quantities.entries()].filter(
      ([id, quantity]) => selectedColourVariantIds.has(String(id)) && quantity > 0
    );
    const totalQuantity = selectedQuantities.reduce((total, [, quantity]) => total + quantity, 0);
    const garmentTotal = selectedQuantities.reduce((total, [id, quantity]) => {
      const variant = this.variants.find((item) => String(item.id) === String(id));
      return total + Number(variant?.price || 0) * quantity;
    }, 0);
    const configuredUnitPrice = Number(this.basePrice || 0) + placementPrice;
    const configuredTotal = garmentTotal + placementPrice * totalQuantity;
    const formattedUnitPrice = this.formatMoney(configuredUnitPrice);
    const currentPrice = this.section.querySelector('[data-current-price]');
    const stickyPrice = this.section.querySelector('[data-sticky-price]');
    const orderTotal = this.section.querySelector('[data-order-total]');
    const orderTotalPrice = this.section.querySelector('[data-order-total-price]');
    if (currentPrice) currentPrice.textContent = formattedUnitPrice;
    if (stickyPrice?.firstChild) stickyPrice.firstChild.textContent = `${formattedUnitPrice} `;
    if (orderTotal) orderTotal.hidden = totalQuantity < 1;
    if (orderTotalPrice) orderTotalPrice.textContent = this.formatMoney(configuredTotal);
    this.section.querySelectorAll('square-placement').forEach((placement) => {
      placement.setAttribute('data-amount', ((totalQuantity > 0 ? configuredTotal : configuredUnitPrice) / 100).toFixed(2));
    });
  }

  bindColourLayout() {
    this.section.querySelectorAll('[data-colour-layout]').forEach((button) => {
      button.addEventListener('click', () => {
        const layout = button.dataset.colourLayout;
        const swatches = this.section.querySelector('[data-colour-swatches]');
        swatches?.classList.toggle('is-list', layout === 'list');
        this.section.querySelectorAll('[data-colour-layout]').forEach((control) => {
          control.classList.toggle('is-active', control === button);
        });
      });
    });
  }

  renderSizeGrid() {
    const container = this.section.querySelector('[data-size-grid]');
    if (!container) return;

    const variants = this.variantsForColour(this.selectedColour);
    const sizes = this.getSizes();
    const bySize = new Map(variants.map((variant) => [variant.options[this.sizeIndex], variant]));

    const sizeOptions = sizes.map((size) => {
      const variant = bySize.get(size);
      if (!variant) return `<div class="tees-polos-product__size-option"><strong>${this.escape(size)}</strong><span>—</span></div>`;
      const value = this.quantities.get(String(variant.id)) || '';
      const maximum = this.maximumQuantity(variant);
      const maxAttribute = maximum === '' ? '' : ` data-max="${maximum}"`;
      return `
        <div class="tees-polos-product__size-option">
          <strong>${this.escape(size)}</strong>
          <button type="button" class="tees-polos-product__stock-cell${value > 0 ? ' has-quantity' : ''}" style="--variant-colour:${this.colourHex(this.selectedColour)};--variant-contrast:${this.contrastColour(this.colourHex(this.selectedColour))}" data-size-cell data-size-value="${this.escape(size)}"${variant.available ? '' : ' disabled'}>
            <input type="text" inputmode="numeric" pattern="[0-9]*" placeholder="Qty"${maxAttribute} value="${value}" data-variant-quantity="${variant.id}" aria-label="${this.escape(size)} quantity"${variant.available ? '' : ' disabled'}>
          </button>
        </div>`;
    }).join('');

    container.innerHTML = `
      <div class="tees-polos-product__size-table" style="--size-count: ${sizes.length}">
        ${sizeOptions}
      </div>`;
  }

  bindQuantityGrid() {
    this.section.querySelector('[data-size-grid]')?.addEventListener('click', (event) => {
      const cell = event.target.closest('[data-size-cell]');
      if (!cell || cell.disabled) return;
      const stickySize = this.section.querySelector('[data-sticky-size-select]');
      if (stickySize && [...stickySize.options].some((option) => option.value === cell.dataset.sizeValue && !option.disabled)) {
        stickySize.value = cell.dataset.sizeValue;
        stickySize.dispatchEvent(new Event('change', { bubbles: true }));
      }
      cell.classList.add('is-editing');
      cell.querySelector('input')?.focus();
    });

    this.section.querySelector('[data-size-grid]')?.addEventListener('input', (event) => {
      if (!event.target.matches('[data-variant-quantity]')) return;
      this.storeQuantity(event.target);
      event.target.closest('[data-size-cell]')?.classList.toggle('has-quantity', Number(event.target.value) > 0);
    });

    this.section.querySelector('[data-size-grid]')?.addEventListener('focusout', (event) => {
      if (!event.target.matches('[data-variant-quantity]')) return;
      const cell = event.target.closest('[data-size-cell]');
      cell?.classList.remove('is-editing');
      if (Number(event.target.value) <= 0) cell?.classList.remove('has-quantity');
    });
  }

  storeQuantity(input) {
    const digitsOnly = String(input.value).replace(/\D/g, '');
    let quantity = Number.parseInt(digitsOnly, 10) || 0;
    const maximum = input.dataset.max ? Number(input.dataset.max) : Infinity;
    if (quantity > maximum) quantity = maximum;
    input.value = quantity > 0 ? String(quantity) : '';
    input.setAttribute('aria-invalid', String(quantity <= 0 && digitsOnly !== ''));
    this.quantities.set(String(input.dataset.variantQuantity), quantity);
    this.updatePrimaryVariant();
    this.updateActionState();
  }

  updateActionState() {
    const selectedColourVariantIds = new Set(
      this.variantsForColour(this.selectedColour).map((variant) => String(variant.id))
    );
    const needsQuantity = ![...this.quantities.entries()].some(
      ([id, quantity]) => selectedColourVariantIds.has(String(id)) && quantity > 0
    );
    this.section.querySelectorAll('[data-tees-polos-submit]').forEach((button) => {
      button.classList.toggle('is-awaiting-quantity', needsQuantity);
      const label = button.querySelector('[data-tees-polos-submit-label]');
      if (label) {
        label.textContent = needsQuantity
          ? button.dataset.emptyLabel || 'Enter Quantity'
          : button.dataset.readyLabel || 'Add to Cart';
      }
    });

    const stickyButton = this.section.querySelector('[data-tees-polos-sticky-add]');
    if (stickyButton) {
      stickyButton.disabled = false;
      stickyButton.classList.toggle('is-awaiting-quantity', needsQuantity);
      const label = stickyButton.querySelector('[data-tees-polos-sticky-label]');
      if (label) {
        label.textContent = needsQuantity
          ? stickyButton.dataset.emptyLabel || 'Enter Quantity'
          : stickyButton.dataset.readyLabel || 'Add to Cart';
      }
    }
  }

  focusQuantityGrid() {
    const quantitySection = this.section.querySelector('.tees-polos-product__sizes');
    const firstInput = quantitySection?.querySelector('[data-variant-quantity]:not(:disabled)');
    if (!quantitySection || !firstInput) return;

    this.section.querySelectorAll('.is-quantity-highlighted').forEach((input) => {
      input.classList.remove('is-quantity-highlighted');
    });
    quantitySection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      firstInput.classList.add('is-quantity-highlighted');
      firstInput.focus({ preventScroll: true });
    }, 450);
    window.setTimeout(() => {
      firstInput.classList.remove('is-quantity-highlighted');
    }, 2450);
  }

  bindStickyQuickAdd() {
    const addButton = this.section.querySelector('[data-tees-polos-sticky-add]');
    if (!addButton) return;

    addButton.addEventListener('click', () => {
      const hasQuantity = [...this.quantities.values()].some((quantity) => quantity > 0);
      if (!hasQuantity) {
        this.focusQuantityGrid();
        return;
      }
      this.form?.requestSubmit();
    });
  }

  refreshStickySizes() {
    const colourSelect = this.section.querySelector('[data-sticky-colour-select]');
    const sizeSelect = this.section.querySelector('[data-sticky-size-select]');
    const addButton = this.section.querySelector('[data-tees-polos-sticky-add]');
    if (!colourSelect || !sizeSelect || !addButton) return;
    const previousSize = sizeSelect.value;
    const variants = this.variantsForColour(colourSelect.value || this.selectedColour);
    sizeSelect.innerHTML = '<option value="">Select size</option>' + variants.map((variant) => {
      const size = variant.options[this.sizeIndex];
      return `<option value="${this.escape(size)}"${variant.available ? '' : ' disabled'}>${this.escape(size)}${variant.available ? '' : ' — Sold out'}</option>`;
    }).join('');
    if ([...sizeSelect.options].some((option) => option.value === previousSize && !option.disabled)) {
      sizeSelect.value = previousSize;
    }
    const variant = this.stickyVariant();
    if (variant) this.updateStickyVariantPrice(variant);
  }

  stickyVariant() {
    const colour = this.section.querySelector('[data-sticky-colour-select]')?.value || this.selectedColour;
    const size = this.section.querySelector('[data-sticky-size-select]')?.value;
    return this.variantsForColour(colour)
      .find((variant) => variant.options[this.sizeIndex] === size && variant.available);
  }

  updateStickyVariantPrice(variant) {
    if (variant) this.updateDisplayedPrice();
  }

  syncStickyVariant(variant) {
    const colourSelect = this.section.querySelector('[data-sticky-colour-select]');
    const sizeSelect = this.section.querySelector('[data-sticky-size-select]');
    if (!variant) return;

    this.updateStickyVariantPrice(variant);
    if (!colourSelect || !sizeSelect) return;

    colourSelect.value = variant.options[this.colourIndex];
    this.refreshStickySizes();
    sizeSelect.value = variant.options[this.sizeIndex];
  }

  async addStickyVariant() {
    const variant = this.stickyVariant();
    const button = this.section.querySelector('[data-tees-polos-sticky-add]');
    if (!variant || !button) return;
    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.showError('');

    try {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const defaultSections = cart?.matches('cart-drawer') ? ['cart-drawer', 'cart-icon-bubble'] : [];
      const sections = cart?.getSectionsToRender?.().map((section) => section.id) || defaultSections;
      const body = { items: [{ id: variant.id, quantity: 1, properties: this.properties() }] };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
        cart.setActiveElement?.(button);
      }
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item to your cart.');
      if (cart?.renderContents && result.sections) {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(result);
      } else {
        window.location.href = this.section.dataset.cartUrl || window.routes?.cart_url || '/cart';
      }
    } catch (error) {
      this.showError(error.message);
    } finally {
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
      button.disabled = !this.stickyVariant();
    }
  }

  updatePrimaryVariant() {
    const colourVariants = this.variantsForColour(this.selectedColour);
    const selectedColourVariantIds = new Set(colourVariants.map((variant) => String(variant.id)));
    const selectedEntry = [...this.quantities.entries()].find(
      ([id, quantity]) => selectedColourVariantIds.has(String(id)) && quantity > 0
    );
    const fallback = colourVariants.find((variant) => variant.available);
    const variant = this.variants.find((item) => String(item.id) === selectedEntry?.[0]) || fallback;
    if (!variant) return;

    const idInput = this.section.querySelector('[data-primary-variant-id]');
    if (idInput) idInput.value = variant.id;
    const sku = this.section.querySelector('[data-product-sku]');
    const fallbackSku = this.section.querySelector('[data-fallback-sku]')?.dataset.fallbackSku;
    if (sku) sku.textContent = variant.sku || fallbackSku || '—';
    this.basePrice = variant.price;
    this.updateDisplayedPrice();
    this.syncStickyVariant(variant);
    this.section.querySelectorAll('pickup-availability').forEach((pickup) => {
      pickup.dataset.variantId = variant.id;
      if (typeof pickup.fetchAvailability === 'function') pickup.fetchAvailability(variant.id);
    });
  }

  bindBulkGrid() {
    const drawer = this.bulkDrawer;
    if (!drawer) return;

    this.section.querySelectorAll('[data-bulk-grid-open]').forEach((button) => {
      button.addEventListener('click', () => {
        this.renderBulkGrid();
        this.positionBulkDrawer();
        this.openLayer(drawer);
        this.loadNextBulkBatch();
      });
    });

    drawer.querySelectorAll('[data-bulk-grid-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeLayer(drawer));
    });

    drawer.querySelectorAll('[data-bulk-gender-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        drawer.querySelectorAll('[data-bulk-gender-tab]').forEach((tab) => {
          const selected = tab === button;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-pressed', String(selected));
        });
      });
    });

    drawer.querySelector('[data-bulk-grid-table]')?.addEventListener('input', (event) => {
      if (!event.target.matches('[data-variant-quantity]')) return;
      this.storeQuantity(event.target);
      event.target.closest('td')?.classList.toggle('has-quantity', Number(event.target.value) > 0);
      this.updateBulkTotals();
    });

    drawer.querySelector('[data-bulk-grid-table]')?.addEventListener('focusout', (event) => {
      if (!event.target.matches('[data-variant-quantity]')) return;
      const cell = event.target.closest('td');
      if (Number(event.target.value) <= 0) cell?.classList.remove('is-editing', 'has-quantity');
    });

    drawer.querySelector('[data-bulk-grid-table]')?.addEventListener('click', (event) => {
      const cell = event.target.closest('td');
      const input = cell?.querySelector('[data-variant-quantity]');
      if (!cell || !input || input.disabled) return;
      cell.classList.add('is-editing');
      input.focus();
      input.select();
    });

    drawer.querySelector('[data-bulk-colour-search]')?.addEventListener('input', async (event) => {
      const query = event.target.value.trim().toLowerCase();
      const clearButton = drawer.querySelector('[data-bulk-clear-search]');
      if (clearButton) clearButton.disabled = query === '';
      if (query !== '') await this.loadBulkSearchMatches(query);
      this.filterBulkRows(query);
    });

    drawer.querySelector('[data-bulk-clear-search]')?.addEventListener('click', (event) => {
      const search = drawer.querySelector('[data-bulk-colour-search]');
      if (!search) return;
      search.value = '';
      event.currentTarget.disabled = true;
      this.filterBulkRows('');
      search.focus();
    });

    drawer.querySelector('[data-bulk-clear]')?.addEventListener('click', () => {
      this.quantities.clear();
      this.renderBulkGrid();
    });

    drawer.querySelector('[data-bulk-grid-table]')?.addEventListener('scroll', (event) => {
      const container = event.currentTarget;
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 240) this.loadNextBulkBatch();
    });

    drawer.querySelector('[data-bulk-grid-apply]')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      const placementRequiresArtwork = this.selectedPlacements().length > 0
        && !this.section.querySelector('[data-placement-artwork-input]')?.files?.length;

      if (!placementRequiresArtwork) {
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');
      }

      window.requestAnimationFrame(() => this.form?.requestSubmit());
    });

    window.addEventListener('resize', () => {
      if (!drawer.hidden) this.positionBulkDrawer();
    });
  }

  async loadNextBulkBatch() {
    if (this.bulkLoading || this.bulkDrawer?.hidden) return;

    const buttons = [...this.section.querySelectorAll('[data-colour-value]')]
      .filter((button) => !this.colourVariantCache.has(button.dataset.colourValue))
      .slice(0, this.bulkBatchSize);

    if (!buttons.length) {
      this.renderBulkGrid();
      return;
    }

    this.bulkLoading = true;
    this.renderBulkGrid();
    await Promise.all(buttons.map((button) => this.loadColourVariants(button)));
    this.bulkLoading = false;
    if (!this.bulkDrawer?.hidden) {
      this.renderBulkGrid();
      const query = this.bulkDrawer.querySelector('[data-bulk-colour-search]')?.value.trim().toLowerCase() || '';
      if (query !== '') {
        this.loadBulkSearchMatches(query);
      } else {
        window.setTimeout(() => this.loadNextBulkBatch(), 60);
      }
    }
  }

  async loadBulkSearchMatches(query) {
    if (this.bulkLoading) return;

    const buttons = [...this.section.querySelectorAll('[data-colour-value]')]
      .filter((button) => button.dataset.colourValue.toLowerCase().includes(query))
      .filter((button) => !this.colourVariantCache.has(button.dataset.colourValue));

    if (!buttons.length) return;
    this.bulkLoading = true;
    this.renderBulkGrid();
    await Promise.all(buttons.map((button) => this.loadColourVariants(button)));
    this.bulkLoading = false;
    if (!this.bulkDrawer?.hidden) this.renderBulkGrid();
  }

  filterBulkRows(query = '') {
    this.bulkDrawer?.querySelectorAll('[data-bulk-colour-row]').forEach((row) => {
      row.hidden = query !== '' && !row.dataset.bulkColourRow.toLowerCase().includes(query);
    });
  }

  renderBulkGrid() {
    const container = this.bulkDrawer?.querySelector('[data-bulk-grid-table]');
    if (!container) return;
    const previousScrollTop = container.scrollTop;
    const query = this.bulkDrawer?.querySelector('[data-bulk-colour-search]')?.value.trim().toLowerCase() || '';
    const sizes = this.getSizes();
    const loadedColours = this.getColours().filter((colour) => this.colourVariantCache.has(colour));
    const rows = loadedColours.map((colour) => {
      const variants = this.variantsForColour(colour);
      const bySize = new Map(variants.map((variant) => [variant.options[this.sizeIndex], variant]));
      const cells = sizes.map((size) => {
        const variant = bySize.get(size);
        const displaySize = this.displaySize(size);
        if (!variant) return `<td><input type="number" placeholder="${this.escape(displaySize)}" aria-label="${this.escape(colour)} ${this.escape(size)} unavailable" disabled></td>`;
        const value = this.quantities.get(String(variant.id)) || '';
        const maximum = this.maximumQuantity(variant);
        const maxAttribute = maximum === '' ? '' : ` data-max="${maximum}"`;
        const colourHex = this.colourHex(colour);
        const contrastColour = this.contrastColour(colourHex);
        return `<td class="${value > 0 ? 'has-quantity' : ''}" style="--variant-colour:${colourHex};--variant-contrast:${contrastColour}"><input type="number" min="0" step="1" placeholder="${this.escape(displaySize)}"${maxAttribute} value="${value}" data-variant-quantity="${variant.id}" aria-label="${this.escape(colour)} ${this.escape(size)} quantity"${variant.available ? '' : ' disabled'}></td>`;
      }).join('');
      return `<tr style="--variant-colour:${this.colourHex(colour)}"><td><span class="tees-polos-product__bulk-colour" style="--bulk-colour:${this.colourHex(colour)}"></span>${this.escape(colour)}</td>${cells}</tr>`;
    }).join('');

    const hasUnloadedColours = this.getColours().some((colour) => !this.colourVariantCache.has(colour));
    const loadingStatus = this.bulkLoading
      ? '<span class="tees-polos-product__bulk-loading-spinner" aria-hidden="true"></span><span>Loading more colours and inventory…</span>'
      : '<span>More colours will load automatically</span>';
    const loadingRow = hasUnloadedColours
      ? `<tr class="tees-polos-product__bulk-loading-row"><td colspan="${sizes.length + 1}">${loadingStatus}</td></tr>`
      : '';
    container.innerHTML = `
      <table class="tees-polos-product__bulk-table">
        <thead><tr><th>COLOUR</th>${sizes.map((size) => `<th>${this.escape(size)}</th>`).join('')}</tr></thead>
        <tbody>${rows}${loadingRow}<tr class="tees-polos-product__bulk-spacer-row" aria-hidden="true"><td colspan="${sizes.length + 1}"></td></tr></tbody>
        <tfoot><tr><th>TOTALS</th>${sizes.map((size) => `<th data-bulk-size-total="${this.escape(size)}">0</th>`).join('')}</tr></tfoot>
      </table>`;
    container.querySelectorAll('tbody tr').forEach((row, index) => {
      if (!row.classList.contains('tees-polos-product__bulk-loading-row') && !row.classList.contains('tees-polos-product__bulk-spacer-row')) {
        row.dataset.bulkColourRow = loadedColours[index];
      }
    });
    container.scrollTop = previousScrollTop;
    this.filterBulkRows(query);
    this.updateBulkTotals();
  }

  positionBulkDrawer() {
    const drawer = this.bulkDrawer;
    if (!drawer) return;

    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    drawer.style.setProperty('--bulk-drawer-top', '0px');
  }

  updateBulkTotals() {
    const sizes = this.getSizes();
    sizes.forEach((size) => {
      const total = this.variants
        .filter((variant) => variant.options[this.sizeIndex] === size)
        .reduce((sum, variant) => sum + (this.quantities.get(String(variant.id)) || 0), 0);
      const output = this.bulkDrawer?.querySelector(`[data-bulk-size-total="${CSS.escape(size)}"]`);
      if (output) output.textContent = total;
    });

    const totalQuantity = [...this.quantities.values()].reduce((sum, quantity) => sum + quantity, 0);
    const instruction = this.bulkDrawer?.querySelector('[data-bulk-instruction]');
    const applyButton = this.bulkDrawer?.querySelector('[data-bulk-grid-apply]');
    const totalPrice = [...this.quantities.entries()].reduce((sum, [id, quantity]) => {
      const variant = this.variants.find((item) => String(item.id) === String(id));
      return sum + Number(variant?.price || 0) * quantity;
    }, 0);
    const price = this.bulkDrawer?.querySelector('[data-bulk-total-price]');
    if (instruction) instruction.textContent = `Total Quantity: ${totalQuantity}`;
    if (price) price.textContent = this.formatMoney(totalPrice);
    if (applyButton) applyButton.disabled = totalQuantity === 0;
    this.updateDisplayedPrice();
  }

  displaySize(size) {
    return ({ XSM: 'XS', SML: 'S', MED: 'M', LRG: 'L', XLG: 'XL' })[String(size).toUpperCase()] || size;
  }

  bindModals() {
    const modals = [...this.section.querySelectorAll('[data-product-modal]')];
    modals.forEach((modal) => document.body.appendChild(modal));

    this.section.querySelectorAll('[data-modal-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const modal = document.querySelector(`[data-product-modal="${CSS.escape(button.dataset.modalOpen)}"]`);
        if (modal) this.openLayer(modal);
      });
    });

    modals.forEach((modal) => {
      modal.querySelectorAll('[data-modal-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeLayer(modal));
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-product-modal]:not([hidden]), [data-bulk-grid]:not([hidden])').forEach((layer) => {
        this.closeLayer(layer);
      });
    });
  }

  openLayer(layer) {
    window.clearTimeout(layer.closeTimer);
    layer.returnFocus = document.activeElement;
    layer.hidden = false;
    document.documentElement.classList.add('tees-polos-modal-open');
    document.body.classList.add('tees-polos-modal-open');
    if (layer.matches('[data-product-modal]')) {
      window.requestAnimationFrame(() => layer.classList.add('is-open'));
    }
    layer.querySelector('[data-bulk-grid-close], .tees-polos-product__modal-close')?.focus();
  }

  closeLayer(layer) {
    const finishClose = () => {
      layer.hidden = true;
      layer.returnFocus?.focus?.();
      if (!document.querySelector('[data-product-modal]:not([hidden]), [data-bulk-grid]:not([hidden])')) {
        document.documentElement.classList.remove('tees-polos-modal-open');
        document.body.classList.remove('tees-polos-modal-open');
      }
    };

    if (layer.matches('[data-product-modal]')) {
      layer.classList.remove('is-open');
      layer.closeTimer = window.setTimeout(finishClose, 280);
      return;
    }

    finishClose();
  }

  bindForm() {
    this.form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const isBulkSubmission = Boolean(this.bulkDrawer && !this.bulkDrawer.hidden);
      const selectedColourVariantIds = new Set(
        this.variantsForColour(this.selectedColour).map((variant) => String(variant.id))
      );
      const mainItems = [...this.quantities.entries()]
        .filter(([id, quantity]) => quantity > 0 && (isBulkSubmission || selectedColourVariantIds.has(String(id))))
        .map(([id, quantity]) => ({ id, quantity, properties: this.properties() }));

      if (!mainItems.length) {
        this.focusQuantityGrid();
        this.showError('Please enter a quantity for at least one size.');
        return;
      }

      const placements = this.selectedPlacements();
      const artwork = this.section.querySelector('[data-placement-artwork]');
      const artworkInput = this.section.querySelector('[data-placement-artwork-input]');

      if (placements.length && !artworkInput?.files?.length) {
        artwork?.classList.add('is-invalid');
        artwork?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => artworkInput?.focus({ preventScroll: true }), 350);
        this.showError('Please upload your artwork before adding this product to the cart.');
        return;
      }

      const items = [];
      mainItems.forEach((mainItem) => {
        items.push(mainItem);

        placements.forEach((placement) => {
          if (!placement.dataset.placementVariantId) return;
          items.push({
            id: placement.dataset.placementVariantId,
            quantity: mainItem.quantity,
            parent_id: mainItem.id,
            properties: {
              'Logo placement for': this.section.querySelector('.business-card-product__content h1')?.textContent.trim() || '',
              '_Parent variant ID': String(mainItem.id)
            }
          });
        });
      });

      const buttons = [
        ...this.section.querySelectorAll('[data-tees-polos-submit], [data-tees-polos-sticky-add]'),
        this.bulkDrawer?.querySelector('[data-bulk-grid-apply]')
      ].filter(Boolean);
      buttons.forEach((button) => {
        const spinner = button.querySelector('.loading__spinner');
        button.classList.add(spinner ? 'loading' : 'is-loading');
        button.disabled = true;
        spinner?.classList.remove('hidden');
      });
      this.showError('');

      try {
        let cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const defaultCartSections = cart?.matches('cart-drawer')
          ? ['cart-drawer', 'cart-icon-bubble']
          : [];
        const cartSections = cart?.getSectionsToRender?.().map((section) => section.id) || defaultCartSections;
        const artworkFile = artworkInput?.files?.[0];
        let requestBody;
        let headers;

        if (artworkFile && placements.length) {
          requestBody = new FormData();
          headers = {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          };
          items.forEach((item, index) => {
            requestBody.append(`items[${index}][id]`, item.id);
            requestBody.append(`items[${index}][quantity]`, item.quantity);
            if (item.parent_id) requestBody.append(`items[${index}][parent_id]`, item.parent_id);
            Object.entries(item.properties || {}).forEach(([name, value]) => {
              requestBody.append(`items[${index}][properties][${name}]`, value);
            });
            if (!item.parent_id) {
              requestBody.append(`items[${index}][properties][Artwork]`, artworkFile, artworkFile.name);
            }
          });
        } else {
          requestBody = { items };
          headers = {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json'
          };
        }

        if (cartSections.length) {
          if (requestBody instanceof FormData) {
            requestBody.append('sections', cartSections.join(','));
            requestBody.append('sections_url', window.location.pathname);
          } else {
            requestBody.sections = cartSections;
            requestBody.sections_url = window.location.pathname;
          }
          cart.setActiveElement?.(event.submitter || document.activeElement);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers,
          body: requestBody instanceof FormData ? requestBody : JSON.stringify(requestBody)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add these items to your cart.');

        if (this.bulkDrawer && !this.bulkDrawer.hidden) {
          this.closeLayer(this.bulkDrawer);
        }

        if (cart?.renderContents && result.sections) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else {
          window.location.href = this.section.dataset.cartUrl || window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        this.showError(error.message);
      } finally {
        buttons.forEach((button) => {
          button.classList.remove('loading', 'is-loading');
          button.disabled = false;
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        });
        this.updateBulkTotals();
        this.updateActionState();
      }
    });
  }

  properties() {
    const properties = {};
    const garmentOption = this.section.querySelector('[data-garment-option]:checked');
    const isBranded = garmentOption?.dataset.garmentOption === 'branded';
    const branding = this.section.querySelector('[data-branding-option]:checked')?.value;
    const placements = this.selectedPlacements().map((placement) => placement.value);
    if (garmentOption?.value) properties['Garment option'] = garmentOption.value;
    if (isBranded && branding) properties['Branding option'] = branding;
    if (isBranded && placements.length) properties['Logo placement'] = placements.join(', ');
    return properties;
  }

  showError(message) {
    const wrapper = this.form?.querySelector('[data-product-error]');
    if (!wrapper) return;
    wrapper.hidden = !message;
    const text = wrapper.querySelector('.product-form__error-message');
    if (text) text.textContent = message;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-NZ', {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'narrowSymbol'
    }).format(Number(cents || 0) / 100);
  }

  escape(value) {
    const element = document.createElement('div');
    element.textContent = String(value ?? '');
    return element.innerHTML;
  }
}

const initialiseTeesPolosProducts = (scope = document) => {
  scope.querySelectorAll('[data-tees-polos-product]').forEach((section) => {
    if (section.dataset.teesPolosInitialised === 'true') return;
    section.dataset.teesPolosInitialised = 'true';
    new TeesPolosProduct(section);
  });
};

document.addEventListener('DOMContentLoaded', () => initialiseTeesPolosProducts());
document.addEventListener('shopify:section:load', (event) => initialiseTeesPolosProducts(event.target));
